import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, Award, Truck, Hotel, CreditCard, Mail, ChevronRight, Phone, FileText, Clock, Star, FileEdit, Upload, Info, Download } from 'lucide-react';
import {
  congressProgram2026,
  type ProgramSession,
  type ProgramSessionType
} from '../data/congressProgram2026';
import { congressEPosters2026 } from '../data/congressEPosters2026';

const congressTopics = [
  {
    title: 'Novosti u liječenju onkoloških bolesnika',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Potporno i palijativno liječenje',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Hitna stanja u onkologiji',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Mogućnosti i značaj genskog profiliranja tumora',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Psihološka podrška onkoloških bolesnika',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Precizna onkologija',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Imunoterapija- novosti u liječenju',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Burnout kod medicinskih sestara u onkologiji',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Standardi i protokoli u onkološkom sestrinstvu',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Digitalni alati i AI u procjeni stanja onkološkog bolesnika',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Hoće li AI promijeniti ulogu medicinske sestre u onkologiji?',
    icon: <Users className="w-6 h-6" />
  },
  {
    title: 'Slobodne teme',
    icon: <Users className="w-6 h-6" />
  }
];

const SHOW_REGISTRATION = true;
const SHOW_SPONSORS = true;
const SHOW_PROGRAM = true;
const SHOW_EPOSTERI = true;

const congressSponsors2026 = [
  { name: 'Pharmamed', src: '/sponsors/pharmamed.jpg' },
  { name: 'Fresenius Kabi', src: '/sponsors/fresenius-kabi.png' },
  { name: 'AstraZeneca', src: '/sponsors/astrazeneca.png' },
  { name: 'Johnson & Johnson', src: '/sponsors/johnson-johnson.jpg' },
  { name: 'Amgen', src: '/sponsors/amgen.jpg' },
  { name: 'Servier', src: '/sponsors/servier.jpg' }
];

const sessionTypeStyles: Record<ProgramSessionType, string> = {
  talk: 'border-l-blue-500 bg-white',
  break: 'border-l-amber-400 bg-amber-50/70',
  satellite: 'border-l-violet-500 bg-violet-50/70',
  workshop: 'border-l-emerald-500 bg-emerald-50/70',
  ceremony: 'border-l-rose-500 bg-rose-50/70',
  social: 'border-l-orange-400 bg-orange-50/70',
  registration: 'border-l-sky-500 bg-sky-50/70',
  meeting: 'border-l-slate-500 bg-slate-50/70'
};

const sessionTypeLabels: Record<ProgramSessionType, string> = {
  talk: 'Predavanje',
  break: 'Stanka',
  satellite: 'Satelitski simpozij',
  workshop: 'Radionica',
  ceremony: 'Svečano',
  social: 'Druženje',
  registration: 'Registracija',
  meeting: 'Sastanak'
};

const sections = [
  { id: 'dobrodisli', title: 'Dobrodošli', icon: <Users className="w-5 h-5" /> },
  { id: 'teme', title: 'Teme kongresa', icon: <Users className="w-5 h-5" /> },
  { id: 'knjiga', title: 'Sažeci', icon: <FileText className="w-5 h-5" /> },
  { id: 'program', title: 'Program', icon: <Calendar className="w-5 h-5" />, hidden: !SHOW_PROGRAM },
  { id: 'sponzori', title: 'Sponzori', icon: <Award className="w-5 h-5" />, hidden: !SHOW_SPONSORS },
  { id: 'eposteri', title: 'E-posteri', icon: <FileText className="w-5 h-5" />, hidden: !SHOW_EPOSTERI },
  { id: 'prezentacije', title: 'Prezentacije', icon: <Award className="w-5 h-5" /> },
  { id: 'upute', title: 'Upute', icon: <FileText className="w-5 h-5" /> },
  { id: 'kotizacije', title: 'Kotizacije', icon: <CreditCard className="w-5 h-5" /> },
  { id: 'bodovanje', title: 'Bodovanje', icon: <Award className="w-5 h-5" /> },
  { id: 'prijevoz', title: 'Prijevoz', icon: <Truck className="w-5 h-5" /> },
  { id: 'smjestaj', title: 'Smještaj', icon: <Hotel className="w-5 h-5" /> },
  { id: 'ostale-info', title: 'Ostale informacije', icon: <Info className="w-5 h-5" /> },
  { id: 'prijave', title: 'Prijave', icon: <FileEdit className="w-5 h-5" />, hidden: !SHOW_REGISTRATION }
].filter((section) => !section.hidden);

export function CongressPage() {
  const [activeProgramDay, setActiveProgramDay] = useState(congressProgram2026[0].id);
  const abstractsPdf = '/Knjiga-sazetaka-HDOMST-2026.pdf';
  const activeDay =
    congressProgram2026.find((day) => day.id === activeProgramDay) || congressProgram2026[0];

  useEffect(() => {
    let hashTimer: ReturnType<typeof setTimeout> | undefined;
    if (window.location.hash) {
      const id = window.location.hash.replace('#', '');
      // Kratki delay da se layout (npr. sticky nav) stigne renderati – bitno za QR linkove
      hashTimer = setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          // Offset iznad sekcije: sticky nav + prostor da se vide tabovi dana
          const offset = id === 'program' ? 160 : 100;
          const top = element.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      }, 150);
    }
    
    // Ukloni loading overlay nakon kratkog vremena
    const timer = setTimeout(() => {
      const pdfLoadingElement = document.getElementById('pdf-loading');
      if (pdfLoadingElement) {
        pdfLoadingElement.style.display = 'none';
      }
      
      const eposteriLoadingElement = document.getElementById('eposteri-loading');
      if (eposteriLoadingElement) {
        eposteriLoadingElement.style.display = 'none';
      }

      const abstractLoadingElement = document.getElementById('abstract-loading');
      if (abstractLoadingElement) {
        abstractLoadingElement.style.display = 'none';
      }
    }, 2000);
    
    return () => {
      clearTimeout(timer);
      if (hashTimer) clearTimeout(hashTimer);
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = sectionId === 'program' ? 160 : 100;
      const top = element.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Navigation Pills */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="sticky top-0 z-50 bg-white/80 backdrop-blur-md shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Desktop Navigation - 2 rows */}
          <div className="hidden lg:block">
            <div className="grid grid-cols-5 gap-2 mb-2">
              {sections.slice(0, 5).map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="flex items-center justify-center space-x-2 px-3 py-2 rounded-full text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors whitespace-nowrap"
                >
                  {section.icon}
                  <span>{section.title}</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-2">
              {sections.slice(5).map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="flex items-center justify-center space-x-2 px-3 py-2 rounded-full text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors whitespace-nowrap"
                >
                  {section.icon}
                  <span>{section.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Tablet Navigation - Single scrollable row */}
          <div className="hidden md:block lg:hidden">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-full text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors whitespace-nowrap flex-shrink-0"
                >
                  {section.icon}
                  <span>{section.title}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Mobile Navigation - 5 columns x 2 rows */}
          <div className="md:hidden">
            <div className="grid grid-cols-5 gap-1 mb-2">
              {sections.slice(0, 5).map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="flex flex-col items-center justify-center p-2 rounded-lg text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  {section.icon}
                  <span className="mt-1 text-center leading-tight text-[10px]">{section.title.split(' ')[0]}</span>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-1">
              {sections.slice(5).map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="flex flex-col items-center justify-center p-2 rounded-lg text-xs font-medium text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                >
                  {section.icon}
                  <span className="mt-1 text-center leading-tight text-[10px]">{section.title.split(' ')[0]}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      <div className="max-w-4xl mx-auto px-3 sm:px-4 py-8 sm:py-12">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-4 sm:p-6 md:p-12 relative overflow-hidden">
              <div className="absolute inset-0 bg-blue-600 opacity-10">
                <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <pattern id="pattern-circles" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                    <circle cx="10" cy="10" r="1.5" fill="currentColor"/>
                  </pattern>
                  <rect x="0" y="0" width="100" height="100" fill="url(#pattern-circles)"/>
                </svg>
              </div>
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="relative z-10 text-center space-y-6"
              >
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                  Kongres Hrvatskog društva onkoloških medicinskih sestara i tehničara
                </h1>
                <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold text-blue-100">
                  s međunarodnim sudjelovanjem
                </h2>
              </motion.div>
            </div>

            <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8 md:space-y-10">
              {/* Date and Location Card */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="grid md:grid-cols-2 gap-6"
              >
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 sm:p-6 rounded-xl space-y-3">
                  <div className="flex items-center space-x-3 text-blue-700">
                    <Calendar className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                    <p className="text-lg sm:text-xl font-semibold">1.- 4. listopada 2026.</p>
                  </div>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 sm:p-6 rounded-xl space-y-3">
                  <div className="flex items-center space-x-3 text-blue-700">
                    <MapPin className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                    <p className="text-lg sm:text-xl font-semibold">Hotel Amphora, Split</p>
                  </div>
                </div>
              </motion.div>

              {/* CTA Button */}
              <motion.div 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 }}
                className="flex justify-center"
              >
                {SHOW_REGISTRATION ? (
                  <Link
                    to="/prijava"
                    className="group relative inline-flex items-center px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg font-bold text-white bg-blue-600 rounded-full overflow-hidden transition-all duration-300 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <span className="relative z-10 flex items-center">
                      <span className="hidden sm:inline">Prijavi se na kongres</span>
                      <span className="sm:hidden">Prijavi se</span>
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                    <div className="absolute inset-0 bg-blue-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300"></div>
                  </Link>
                ) : (
                  <div className="inline-flex flex-col items-center text-center bg-white/80 text-blue-800 px-6 sm:px-8 py-4 rounded-2xl border border-blue-200 shadow-sm">
                    <p className="text-lg font-semibold">Prijave su privremeno zatvorene</p>
                    <p className="text-sm text-blue-600 mt-1">Pratite nas za nove informacije</p>
                  </div>
                )}
              </motion.div>

              {/* Dobrodošli */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                id="dobrodisli"
                className="prose max-w-none"
              >
                <div className="bg-gradient-to-r from-blue-50 to-white p-4 sm:p-6 md:p-8 rounded-xl border border-blue-100">
                  <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6">Dobrodošli</h3>
                  <div className="text-gray-700 text-base sm:text-lg leading-relaxed space-y-4">
                    <p>
                      Poštovane kolegice i kolege, dame i gospodo, dragi prijatelji,
                      članovi Hrvatskog društva onkoloških medicinskih sestara i tehničara (HDOMST),
                    </p>
                    <p>
                      veliko nam je zadovoljstvo i čast pozvati Vas na <strong>6. KONGRES HRVATSKOG DRUŠTVA ONKOLOŠKIH MEDICINSKIH SESTARA I TEHNIČARA (HDOMST)</strong>. Želja nam je i ovoga puta okupiti medicinske sestre i tehničare, liječnike, farmaceute i sve ostale zdravstvene djelatnike koji svojim znanjem, iskustvom i predanošću čine neizostavan dio multidisciplinarnih onkoloških timova.
                    </p>
                    <p>
                      Onkologija je jedno od najdinamičnijih područja medicine, u kojem svakodnevno svjedočimo značajnim naprecima u dijagnostici, liječenju i skrbi za onkološke bolesnike. U tom procesu uloga onkoloških medicinskih sestara i tehničara od iznimne je važnosti – od pružanja stručne zdravstvene njege, preko edukacije bolesnika i njihovih obitelji, do aktivnog sudjelovanja u timskom radu s ciljem postizanja najboljih ishoda liječenja.
                    </p>
                    <p>
                      Kroz suradnju, razmjenu znanja i iskustava te kontinuirano stručno usavršavanje možemo dodatno unaprijediti kvalitetu skrbi za naše bolesnike.
                    </p>
                    <p>
                      Radujemo se Vašem dolasku i aktivnom sudjelovanju na 6. Kongresu HDOMST.
                    </p>
                    <p className="pt-2">
                      Do susreta s Vama,
                    </p>
                    <p className="font-semibold text-blue-900">
                      Jasminka Miličević, mag. med. techn.<br />
                      predsjednica HDOMST-a
                    </p>
                  </div>
                  <div className="flex items-start sm:items-center space-x-2 text-blue-600 font-medium mt-4 pt-4 border-t border-blue-100">
                    <Users className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" />
                    <span className="text-sm sm:text-base">Kongres će se održavati uživo u Splitu, Hotel Amphora.</span>
                  </div>
                </div>
              </motion.div>

              {/* Topics */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                id="teme"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6 flex items-center">
                  <Users className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Teme kongresa
                </h3>
                <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
                  {congressTopics.map((topic, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 * index }}
                      className="flex items-start space-x-3 p-3 sm:p-4 bg-gradient-to-r from-blue-50 to-white rounded-lg hover:shadow-md transition-shadow"
                    >
                      {topic.icon}
                      <span className="text-sm sm:text-base text-gray-700">{topic.title}</span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Book of Abstracts */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.74 }}
                id="knjiga"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6 flex items-center">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Knjiga sažetaka
                </h3>
                <div className="bg-gradient-to-r from-blue-50 to-white p-4 sm:p-6 rounded-xl border border-blue-100">
                  <div className="space-y-4">
                    <p className="text-gray-700 text-base sm:text-lg">
                      Pregledajte Knjigu sažetaka s objavljenim radovima i e-posterima.
                    </p>
                    <div className="bg-white rounded-lg border-2 border-blue-200 overflow-hidden">
                      <div className="bg-blue-600 text-white p-3 md:p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-center space-x-2">
                            <FileText className="w-5 h-5 flex-shrink-0" />
                            <span className="font-semibold text-sm md:text-base">Knjiga sažetaka HDOMST 2026</span>
                          </div>
                          <a 
                            href={abstractsPdf} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center justify-center space-x-2 bg-white/20 hover:bg-white/30 px-3 py-2 md:px-4 rounded-lg transition-colors text-sm md:text-base"
                          >
                            <FileText className="w-4 h-4" />
                            <span className="hidden sm:inline">Otvori u novom tabu</span>
                            <span className="sm:hidden">Otvori PDF</span>
                          </a>
                        </div>
                      </div>

                      <div className="md:hidden bg-blue-50 border-b border-blue-200 p-4">
                        <div className="flex items-start space-x-3">
                          <div className="text-blue-600 mt-1">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-blue-800 font-medium mb-1">
                              Savjet za mobilne uređaje
                            </p>
                            <p className="text-xs text-blue-600">
                              Za bolje iskustvo čitanja sažetaka, preporučujemo otvaranje u novom tabu ili preuzimanje PDF-a.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="relative">
                        <div className="h-64 sm:h-80 md:hidden">
                          <iframe
                            src={`${abstractsPdf}#zoom=fit`}
                            className="w-full h-full border-0"
                            title="Knjiga sažetaka HDOMST 2026"
                            loading="lazy"
                          />
                        </div>
                        <div className="hidden md:block h-[500px] lg:h-[600px]">
                          <iframe
                            src={`${abstractsPdf}#zoom=fit`}
                            className="w-full h-full border-0"
                            title="Knjiga sažetaka HDOMST 2026"
                            loading="lazy"
                          />
                        </div>
                        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center md:hidden" id="abstract-loading">
                          <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                            <p className="text-sm text-gray-600">Učitavam Knjigu sažetaka...</p>
                          </div>
                        </div>
                      </div>

                      <div className="md:hidden bg-gray-50 border-t border-gray-200 p-4">
                        <div className="grid grid-cols-1 gap-3">
                          <a 
                            href={abstractsPdf} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-3 rounded-lg font-medium transition-colors hover:bg-blue-700"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                            <span>Otvori u punoj veličini</span>
                          </a>
                          <a 
                            href={abstractsPdf} 
                            download="Knjiga-sazetaka-HDOMST-2026.pdf"
                            className="flex items-center justify-center space-x-2 bg-gray-600 text-white px-4 py-3 rounded-lg font-medium transition-colors hover:bg-gray-700"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <span>Preuzmi PDF</span>
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="hidden md:flex items-center justify-center space-x-4 text-sm text-gray-600 mt-4">
                      <span>📄 Knjiga sažetaka u PDF formatu</span>
                      <span>•</span>
                      <span>🔍 Možete zumirati za bolje čitanje</span>
                      <span>•</span>
                      <span>💾 Dostupno za preuzimanje</span>
                    </div>

                    <div className="md:hidden mt-4">
                      <div className="grid grid-cols-1 gap-2 text-xs text-gray-600 text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <span>📄</span>
                          <span>PDF format</span>
                        </div>
                        <div className="flex items-center justify-center space-x-2">
                          <span>🔍</span>
                          <span>Zumiranje dostupno</span>
                        </div>
                        <div className="flex items-center justify-center space-x-2">
                          <span>💾</span>
                          <span>Dostupno za preuzimanje</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Program */}
              {SHOW_PROGRAM && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75 }}
                id="program"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-2 sm:mb-3 flex items-center">
                  <Calendar className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Program
                </h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4">
                  Raspored 6. Kongresa HDOMST · 1.–4. listopada 2026. · Hotel Amphora, Split
                </p>

                <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
                  <a
                    href="/HDOMST-PROGRAM-2026.pdf"
                    download="HDOMST-PROGRAM-2026.pdf"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm transition-colors"
                  >
                    <Download className="w-4 h-4 flex-shrink-0" />
                    Preuzmi program (PDF)
                  </a>
                  <a
                    href="/HDOMST-PROGRAM-2026.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-blue-200 text-blue-800 hover:bg-blue-50 text-sm font-medium transition-colors"
                  >
                    <FileText className="w-4 h-4 flex-shrink-0" />
                    Otvori PDF u novom tabu
                  </a>
                </div>

                <div className="flex gap-2 overflow-x-auto pb-2 mb-4 sm:mb-6 -mx-1 px-1">
                  {congressProgram2026.map((day) => {
                    const isActive = day.id === activeProgramDay;
                    return (
                      <button
                        key={day.id}
                        type="button"
                        onClick={() => setActiveProgramDay(day.id)}
                        className={`flex-shrink-0 px-3 sm:px-4 py-2.5 rounded-xl border text-left transition-colors ${
                          isActive
                            ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                            : 'bg-white border-blue-100 text-blue-900 hover:border-blue-300 hover:bg-blue-50'
                        }`}
                      >
                        <div className={`text-xs sm:text-sm font-semibold ${isActive ? 'text-white' : 'text-blue-900'}`}>
                          {day.dayHr}
                        </div>
                        <div className={`text-[11px] sm:text-xs ${isActive ? 'text-blue-100' : 'text-gray-500'}`}>
                          {day.date}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mb-4 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 sm:px-5 sm:py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                    <h4 className="text-lg sm:text-xl font-bold">
                      {activeDay.dayHr} / {activeDay.dayEn}
                    </h4>
                    <p className="text-blue-100 text-sm sm:text-base">{activeDay.date}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {activeDay.sessions.map((session: ProgramSession, index: number) => (
                    <div
                      key={`${activeDay.id}-${index}`}
                      className={`rounded-xl border border-blue-100 border-l-4 p-3 sm:p-4 ${sessionTypeStyles[session.type]}`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                        <div className="sm:w-36 flex-shrink-0">
                          <div className="inline-flex items-center gap-1.5 text-blue-800 font-semibold text-sm bg-white/80 border border-blue-100 rounded-lg px-2.5 py-1">
                            <Clock className="w-3.5 h-3.5 flex-shrink-0" />
                            <span>{session.time}</span>
                          </div>
                          {session.type !== 'talk' && (
                            <div className="mt-2 text-[11px] uppercase tracking-wide font-semibold text-gray-500">
                              {sessionTypeLabels[session.type]}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h5 className="text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                            {session.titleHr}
                          </h5>
                          {session.titleEn && (
                            <p className="mt-1 text-xs sm:text-sm text-gray-500 italic leading-snug">
                              {session.titleEn}
                            </p>
                          )}
                          {session.authors && (
                            <p className="mt-2 text-xs sm:text-sm text-blue-800 font-medium">
                              {session.authors}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-5 pt-4 border-t border-blue-100">
                  <p className="text-xs sm:text-sm text-gray-500">
                    Legenda: predavanja, satelitski simpoziji, radionice, stanke, svečani dijelovi i sastanci označeni su bojama lijeve trake.
                  </p>
                </div>
              </motion.div>
              )}

              {/* Sponzori kongresa */}
              {SHOW_SPONSORS && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.755 }}
                id="sponzori"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-2 sm:mb-3 flex items-center">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Sponzori kongresa
                </h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">
                  Zahvaljujemo sponzorima 6. Kongresa HDOMST
                </p>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
                  {congressSponsors2026.map((sponsor) => (
                    <div
                      key={sponsor.name}
                      className="flex items-center justify-center bg-white border border-gray-100 rounded-xl p-4 sm:p-6 h-28 sm:h-36"
                    >
                      <img
                        src={sponsor.src}
                        alt={sponsor.name}
                        className="max-h-16 sm:max-h-20 max-w-full w-auto object-contain"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </motion.div>
              )}

              {/* E-posteri */}
              {SHOW_EPOSTERI && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.76 }}
                id="eposteri"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-2 sm:mb-3 flex items-center">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  E-posteri
                </h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6">
                  Potvrđeni e-posteri 6. Kongresa HDOMST ({congressEPosters2026.length})
                </p>

                <div className="space-y-3">
                  {congressEPosters2026.map((poster) => (
                    <div
                      key={poster.number}
                      className="rounded-xl border border-blue-100 border-l-4 border-l-blue-500 bg-white p-3 sm:p-4"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                        <div className="flex-shrink-0">
                          <div className="inline-flex items-center justify-center min-w-[2.5rem] h-10 px-2 rounded-lg bg-blue-600 text-white font-bold text-sm">
                            {poster.number}
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h5 className="text-sm sm:text-base font-semibold text-gray-900 leading-snug">
                            {poster.titleHr}
                          </h5>
                          <p className="mt-1 text-xs sm:text-sm text-gray-500 italic leading-snug">
                            {poster.titleEn}
                          </p>
                          <p className="mt-2 text-xs sm:text-sm text-blue-800 font-medium">
                            {poster.authors}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
              )}

              {/* Presentations */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.77 }}
                id="prezentacije"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6 flex items-center">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Prezentacije i sažetci
                </h3>
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                    <p className="text-gray-700 mb-3">
                      Prijavu sažetaka usmenih prezentacija i e postera možete prijaviti na linku:
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <p className="text-blue-600 font-semibold text-sm sm:text-base">www.hdomst.eu</p>
                      <Link
                        to="/prijava-sazetka"
                        className="inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base text-white bg-blue-600 rounded-lg font-medium transition-all duration-200 hover:bg-blue-700"
                      >
                        <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        <span className="hidden sm:inline">Prijavi sažetak</span>
                        <span className="sm:hidden">Prijavi</span>
                      </Link>
                    </div>
                    <p className="text-gray-700 mt-3">Rok za predaju sažetaka:</p>
                    <p className="text-xl font-bold text-blue-900">15.srpnja 2026.</p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-xl border border-blue-100 mt-4">
                    <p className="text-gray-700 mb-3">
                      Predaju gotovih prezentacija i e postera možete prijaviti na linku:
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <p className="text-blue-600 font-semibold text-sm sm:text-base">www.hdomst.eu</p>
                      <Link
                        to="/upload-prezentacije"
                        className="inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base text-white bg-blue-600 rounded-lg font-medium transition-all duration-200 hover:bg-blue-700"
                      >
                        <Upload className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                        <span className="hidden sm:inline">Upload prezentacije</span>
                        <span className="sm:hidden">Upload</span>
                      </Link>
                    </div>
                    <p className="text-gray-700 mt-3">Rok za predaju prezentacija i e postera:</p>
                    <p className="text-xl font-bold text-blue-900">10.rujna 2026.</p>
                  </div>
                </div>
              </motion.div>

              {/* Instructions */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                id="upute"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6 flex items-center">
                  <FileText className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Upute
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                    <h4 className="text-xl font-semibold text-blue-900 mb-4">UPUTE ZA PISANJE SAŽETAKA:</h4>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>sažetak mora sadržavati od 200 do 350 riječi</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>sažetak mora biti napisan na standardnom hrvatskom jeziku</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>prezime i početno slovo imena autora i koautora pišu se bez titule</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>sažetak mora sadržavati naziv institucije te grada autora</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>sažetak treba sadržavati e-mail adresu autora</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>tekst sažetka se sastoji od: cilja, metoda, rezultata, zaključka za istraživačke radove</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>prethodno objavljeni sažetak neće biti prihvaćen</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                    <h4 className="text-xl font-semibold text-blue-900 mb-4">UPUTE ZA PISANJE E POSTERA:</h4>
                    <ul className="space-y-3 text-gray-700">
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>e poster mora sadržavati samo jedan slide na kojem je prikazan cijeli sadržaj e postera</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>potrebno je koristiti fontove kompatibilne s PC i Mac operativnim sustavima (Arial, Courier, Georgia, Helvetica, Times new Roman, Times...)</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>ne koristite fontove veličine manje od 10pt</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>veličina datoteke mora biti manja od 20 MB</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-600 mr-2">•</span>
                        <span>e posteri moraju biti izrađeni u formatu portreta/uspravni; 25,35 x 50,80 cm</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mt-6 space-y-4 bg-gradient-to-r from-blue-50 to-white p-4 sm:p-6 rounded-xl border border-blue-100">
                  <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
                    Pozivamo vas, prijavite što veći broj postera, prije svega one kojima ćete prezentirati vlastite rezultate, jer će prvi autori triju odabranih postera biti nagrađeni.
                  </p>
                  <p className="text-gray-700 text-base sm:text-lg">
                    <strong>Napomena:</strong> slanje sažetaka ne podrazumijeva ujedno i prijavu za sudjelovanje.
                  </p>
                  <p className="text-gray-700 text-base sm:text-lg">
                    Prijava sažetaka u standardnom obliku obavlja se online, preko online sustava.
                  </p>
                  <p className="text-gray-700 text-base sm:text-lg">
                    Sažetci neće biti lektorirani – za jezičnu i sadržajnu ispravnost odgovara autor.
                  </p>
                </div>
              </motion.div>

              {/* Registration */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1 }}
                id="kotizacije"
                className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-4 sm:p-6 md:p-8"
              >
                <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center">
                  <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Kotizacije
                </h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div className="bg-white/10 rounded-xl p-4 sm:p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <Calendar className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm sm:text-base">Rana kotizacija</p>
                        <p className="text-xs sm:text-sm text-blue-100">do 15.8.2026.</p>
                      </div>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold">250,00 EUR</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 sm:p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <Calendar className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm sm:text-base">Kasna kotizacija</p>
                        <p className="text-xs sm:text-sm text-blue-100">nakon 15.8.2026.</p>
                      </div>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold">350,00 EUR</p>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 sm:p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <Calendar className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm sm:text-base">Dnevna kotizacija</p>
                      </div>
                    </div>
                    <p className="text-2xl sm:text-3xl font-bold">200,00 EUR</p>
                  </div>
                </div>

                <div className="mt-6 sm:mt-8 grid sm:grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                  <div className="bg-white/10 rounded-xl p-4 sm:p-6">
                    <h4 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">KOTIZACIJA ZA SUDIONIKE UKLJUČUJE:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>torbu s kongresnim materijalima</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>finalni program</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>Knjigu sažetaka</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>sudjelovanje na predavanjima prema programu</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>pristup zoni izložbenog prostora</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>sudjelovanje na svečanom otvaranju Kongresa</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>stanke za kavu</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>HKMS/HLK bodove</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>potvrda o sudjelovanju</span>
                      </li>
                    </ul>
                  </div>
                  <div className="bg-white/10 rounded-xl p-4 sm:p-6">
                    <h4 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">DNEVNA KOTIZACIJA UKLJUČUJE:</h4>
                    <ul className="space-y-2">
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>torbu s kongresnim materijalima</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>finalni program</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>Knjigu sažetaka</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>sudjelovanje na predavanjima određeni dan</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>pristup zoni izložbenog prostora</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>stanke za kavu</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>ručak i večeru</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>HKMS/HLK bodove</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-blue-300 mr-2">•</span>
                        <span>potvrda o sudjelovanju</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mt-6 bg-white/10 rounded-lg p-4 sm:p-6">
                  <p className="font-mono text-sm sm:text-base lg:text-lg mb-2 break-all">IBAN: HR9623600001102178796</p>
                  <p className="text-blue-100 text-sm sm:text-base">S naznakom "Kotizacija za kongres 2026"</p>
                </div>
              </motion.div>

              {/* Points/Bodovanje */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
                id="bodovanje"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6 flex items-center">
                  <Award className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Bodovanje
                </h3>
                <div className="space-y-6">
                  <p className="text-gray-700">
                    Kongres je bodovan od strane Hrvatske komore medicinskih sestara.
                  </p>
                  <p className="text-gray-700">
                    Za sudjelovanje na Kongresu sudionici će biti bodovani prema pravilniku HKMS/HLK.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                      <div className="flex items-center space-x-2 text-blue-700 mb-3">
                        <Award className="w-5 h-5" />
                        <span className="font-semibold">Aktivno sudjelovanje</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-900">15 bodova</p>
                      <p className="text-sm text-gray-600 mt-1">Za predavače</p>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                      <div className="flex items-center space-x-2 text-blue-700 mb-3">
                        <Award className="w-5 h-5" />
                        <span className="font-semibold">Pasivno sudjelovanje</span>
                      </div>
                      <p className="text-2xl font-bold text-blue-900">10 bodova</p>
                      <p className="text-sm text-gray-600 mt-1">Za sudionike</p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Transport */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.9 }}
                id="prijevoz"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6 flex items-center">
                  <Truck className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Prijevoz
                </h3>
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                    <h4 className="text-xl font-semibold text-blue-900 mb-4">Organizirani prijevoz</h4>
                    <p className="text-gray-700 mb-3">
                      Organiziramo prijevoz na relaciji: Zagreb - Split - Zagreb
                    </p>
                    
                    <div className="space-y-2 mt-4">
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <p className="text-gray-700">
                          <span className="font-semibold">Obavezna prijava:</span> do 1. rujna 2026.
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <p className="text-gray-700">
                          <span className="font-semibold">Vrijeme polaska:</span> 9:00
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <MapPin className="w-5 h-5 text-blue-600" />
                        <p className="text-gray-700">
                          <span className="font-semibold">Mjesto polaska:</span> Zagrebački velesajam - južni ulaz
                        </p>
                      </div>
                    </div>
                    
                    <div className="mt-4">
                      <p className="text-gray-700">Prijave na linku: <span className="text-blue-600 font-semibold">www.hdomst.eu</span></p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Accommodation */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.95 }}
                id="smjestaj"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6 flex items-center">
                  <Hotel className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Smještaj
                </h3>
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-white p-6 rounded-xl border border-blue-100">
                    <h4 className="text-xl font-semibold text-blue-900 mb-4 text-center">Hotel Amphora</h4>
                    <p className="text-xl font-semibold text-blue-900 mb-4 text-center">Split</p>
                    
                    <div className="flex justify-center mb-6">
                      <div className="flex">
                        <Star className="w-5 h-5 text-yellow-400 fill-current" />
                        <Star className="w-5 h-5 text-yellow-400 fill-current" />
                        <Star className="w-5 h-5 text-yellow-400 fill-current" />
                        <Star className="w-5 h-5 text-yellow-400 fill-current" />
                      </div>
                      <span className="ml-2 text-gray-600">4 zvjezdice</span>
                    </div>
                    
                    <div className="flex justify-center mb-8">
                      <img 
                        src="/images/hotel-amphora.jpg" 
                        alt="Hotel Amphora" 
                        className="rounded-lg shadow-md max-h-60 object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.src = "https://placehold.co/600x400/e6f0ff/2563eb?text=Hotel+Amphora";
                        }}
                      />
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <div className="bg-blue-50 p-5 rounded-xl text-center">
                        <p className="text-2xl font-bold text-blue-800 mb-3">280,00 EUR</p>
                        <div className="bg-blue-500 text-white p-3 rounded-lg">
                          <p className="font-medium">Jedno noćenje po osobi u jednokrevetnoj sobi</p>
                        </div>
                      </div>
                      
                      <div className="bg-blue-50 p-5 rounded-xl text-center">
                        <p className="text-2xl font-bold text-blue-800 mb-3">350,00 EUR</p>
                        <div className="bg-blue-500 text-white p-3 rounded-lg">
                          <p className="font-medium">Jedno noćenje za dvije osobe u dvokrevetnoj sobi</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h5 className="font-semibold text-blue-900 mb-3">CIJENA UKLJUČUJE:</h5>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span>pripadajući PDV</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span>boravišnu pristojbu</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span>korištenje wi-fi-a</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span>sve dodatne troškove gosti plaćaju na licu mjesta</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span>doručak, ručak i večeru</span>
                          </li>
                        </ul>
                      </div>
                      
                      <div>
                        <h5 className="font-semibold text-blue-900 mb-3">UVJETI OTKAZIVANJA:</h5>
                        <ul className="space-y-2 text-gray-700">
                          <li className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span>3 - 1 dan ili nedolazak gosta (NO SHOW) - 100%</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span>4 - 9 dana- 75%</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span>10 - 14 dana- 50%</span>
                          </li>
                          <li className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span>15 - 59 dana- 25%</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 space-y-3 text-gray-700 text-base sm:text-lg">
                  <p><strong>Rok za prijavu smještaja je 10.9.2026.</strong></p>
                  <p>
                    Parkiralište je dostupno u blizini objekta i nije potrebna rezervacija.
                    Parking se naplaćuje na recepciji hotela.
                  </p>
                </div>
              </motion.div>

              {/* Ostale informacije */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.98 }}
                id="ostale-info"
                className="bg-white rounded-xl border border-blue-100 p-4 sm:p-6 md:p-8 shadow-sm"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6 flex items-center">
                  <Info className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                  Ostale informacije
                </h3>
                <div className="space-y-8">
                  <div className="bg-gradient-to-r from-blue-50 to-white p-4 sm:p-6 rounded-xl border border-blue-100">
                    <h4 className="text-lg sm:text-xl font-semibold text-blue-900 mb-4">Zaštita i obrada osobnih podataka</h4>
                    <div className="text-gray-700 text-sm sm:text-base leading-relaxed space-y-4">
                      <p>
                        Osobni podaci prikupljeni prilikom prijave za sudjelovanje na kongresu Hrvatskog društva onkoloških medicinskih sestara i tehničara (u daljnjem tekstu: Kongres) smatraju se osobnim podacima te se obrađuju u skladu s Uredbom (EU) 2016/679 Europskog parlamenta i Vijeća (Opća uredba o zaštiti podataka – GDPR) te Zakonom o provedbi Opće uredbe o zaštiti osobnih podataka (NN 48/2018).
                      </p>
                      <p>
                        Podaci koje sudionici dostave putem prijavnog obrasca (u daljnjem tekstu: Prijava) pohranjuju se na siguran način u bazu podataka društva Studio Arena d.o.o., Ulica dr. Luje Naletilića 94B, Zagreb (u daljnjem tekstu: Studio Arena).
                      </p>
                      <p>
                        Ispunjavanjem i slanjem Prijave sudionik daje suglasnost za obradu svojih osobnih podataka isključivo u svrhu organizacije i provedbe Kongresa. Dostava traženih osobnih podataka preduvjet je za uspješnu prijavu; u slučaju njihovog nedostavljanja, sudjelovanje na Kongresu neće biti moguće.
                      </p>
                      <p>
                        Studio Arena prikuplja i obrađuje osobne podatke u ime Hrvatskog društva onkoloških medicinskih sestara i tehničara, kao voditelja obrade osobnih podataka. Podaci se koriste isključivo radi ostvarivanja prava i obveza vezanih uz sudjelovanje na Kongresu, uključujući administraciju prijava, organizacijske aktivnosti, eventualni smještaj te izdavanje računa za kotizaciju i povezane usluge.
                      </p>
                      <p>
                        Sudionici imaju pravo u svakom trenutku zatražiti pristup svojim osobnim podacima, njihov ispravak ili brisanje, ograničenje obrade, kao i pravo na prenosivost podataka, sukladno važećim propisima.
                      </p>
                      <p>
                        Voditelj obrade i Studio Arena provode odgovarajuće tehničke i organizacijske mjere radi osiguranja povjerljivosti i sigurnosti osobnih podataka te zaštite od neovlaštenog pristupa, gubitka ili zlouporabe. Osobni podaci neće se koristiti u druge svrhe niti prosljeđivati trećim osobama, osim kada je to nužno radi provedbe Kongresa ili temeljem zakonske obveze.
                      </p>
                      <p>
                        Podaci se čuvaju onoliko dugo koliko je potrebno za ostvarenje svrhe obrade, nakon čega se brišu ili vraćaju voditelju obrade u skladu s važećim propisima.
                      </p>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-blue-50 to-white p-4 sm:p-6 rounded-xl border border-blue-100">
                    <h4 className="text-lg sm:text-xl font-semibold text-blue-900 mb-4">Dodatne napomene</h4>
                    <div className="text-gray-700 text-sm sm:text-base leading-relaxed space-y-4">
                      <p>
                        Organizator može prikupljati i analizirati podatke o sudjelovanju na pojedinim predavanjima isključivo u svrhu statističke obrade i unapređenja kvalitete budućih kongresa.
                      </p>
                      <p>
                        Tijekom Kongresa mogu se izrađivati fotografije i/ili videozapisi. Sudionici koji ne žele biti fotografirani ili snimani mogu se obratiti organizatoru radi dodatnih informacija.
                      </p>
                      <p>
                        Prijavom na Kongres sudionik je upoznat s činjenicom da se fotografije i snimke nastale tijekom događanja mogu koristiti za interne potrebe Društva te u svrhu promocije Kongresa, temeljem legitimnog interesa organizatora.
                      </p>
                      <p>
                        Organizator zadržava pravo izmjene ovih uvjeta u bilo kojem trenutku.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Registration Applications */}
              {SHOW_REGISTRATION && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  id="prijave"
                  className="bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-xl p-4 sm:p-6 md:p-8"
                >
                  <h3 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 flex items-center">
                    <FileEdit className="w-5 h-5 sm:w-6 sm:h-6 mr-2 flex-shrink-0" />
                    Prijave
                  </h3>
                  <div className="space-y-4 sm:space-y-6">
                    <p className="text-base sm:text-lg">
                      Prijave za Kongres i smještaj vrše se na web stranici
                    </p>
                    <p className="text-lg sm:text-xl font-bold text-center bg-white/10 py-3 rounded-lg break-all">
                      www.hdomst.eu
                    </p>
                    <p className="text-center text-sm sm:text-base">ili direktno kod tehničkog organizatora</p>
                    
                    <div className="bg-white/10 rounded-xl p-4 sm:p-6 mt-4 sm:mt-6">
                      <div className="space-y-3">
                        <p className="font-semibold">Studio Arena d.o.o.</p>
                        <p>Ul. dr. Luje Naletilića 94b</p>
                        <p>10000 Zagreb</p>
                        <div className="flex items-center space-x-2 mt-3">
                          <Mail className="w-5 h-5" />
                          <a href="mailto:info@studio-arena.hr" className="text-blue-100 hover:text-white hover:underline">
                            info@studio-arena.hr
                          </a>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Phone className="w-5 h-5" />
                          <a href="tel:+385952551346" className="text-blue-100 hover:text-white hover:underline">
                            095/255-1346
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Contact */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.05 }}
                className="border-t border-blue-100 pt-8"
              >
                <h3 className="text-xl sm:text-2xl font-bold text-blue-900 mb-4 sm:mb-6 flex items-center">
                  <Mail className="w-6 h-6 mr-2" />
                  Kontakt i informacije
                </h3>
                <div className="bg-gradient-to-r from-blue-50 to-white rounded-xl p-6 border border-blue-100">
                  <div className="space-y-4">
                    <div>
                      <p className="font-semibold text-gray-900 mb-2">Tehnički organizator:</p>
                      <div className="flex items-start space-x-3">
                        <MapPin className="w-5 h-5 text-blue-600 mt-1" />
                        <div>
                          <p className="font-semibold text-gray-900">Studio Arena d.o.o.</p>
                          <p className="text-gray-600">Ulica dr. Luje Naletilića 94b</p>
                          <p className="text-gray-600">10000 Zagreb</p>
                          <p className="text-gray-600">OIB: 28869851978</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3 text-blue-600">
                      <Mail className="w-5 h-5" />
                      <a href="mailto:info@studio-arena.hr" className="hover:underline">info@studio-arena.hr</a>
                    </div>
                    <div className="flex items-center space-x-3 text-blue-600">
                      <Phone className="w-5 h-5" />
                      <a href="tel:+385952551346" className="hover:underline">+385952551346</a>
                    </div>
                    <div className="flex items-center space-x-3 text-blue-600">
                      <MapPin className="w-5 h-5" />
                      <span>Hotel Amphora, Split</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
} 